import React, { useState } from "react";
import { Copy, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const SNIPPET = `<!-- GenQuotes embed -->
<iframe
  src="https://app.genquotes.com/widget/YOUR_WIDGET_ID"
  width="100%"
  height="640"
  frameborder="0"
  style="border:0;display:block;max-width:100%;">
</iframe>`;

const PLATFORMS = [
  {
    id: "wordpress",
    name: "WordPress",
    steps: [
      { t: "Open your page or post editor", d: "Navigate to Pages or Posts in WP Admin and open (or create) the page where you want the quote widget to appear." },
      { t: "Add a Custom HTML block", d: "Click the + button → search for 'Custom HTML' → insert it where the widget should appear on the page." },
      { t: "Paste the embed snippet", d: "Paste the snippet below into the Custom HTML block. Replace YOUR_WIDGET_ID with the ID from your GenQuotes account." },
      { t: "Update / Publish", d: "Click Update (or Publish). Preview the page on desktop and mobile — the widget should render full-width inside your layout." },
    ],
  },
  {
    id: "squarespace",
    name: "Squarespace",
    steps: [
      { t: "Open the page in the editor", d: "Pages → click the page where you want the widget → click Edit." },
      { t: "Add a Code block", d: "Click the + insert point → Code (under MORE). Squarespace will insert a Code block." },
      { t: "Paste the snippet", d: "Click into the Code block → paste your embed snippet. Make sure 'HTML' is selected (not Markdown) and 'Display Source' is OFF." },
      { t: "Save", d: "Click Save in the top-left. View the live page to confirm the widget renders correctly." },
    ],
  },
  {
    id: "wix",
    name: "Wix",
    steps: [
      { t: "Open the Wix Editor", d: "Sign in → Site → Edit Site. Navigate to the page where the widget should appear." },
      { t: "Add an HTML embed element", d: "Click the + on the left toolbar → Embed → Embed a Widget → Embed HTML. Drag it onto the page." },
      { t: "Paste the snippet", d: "In the right-hand settings panel choose 'Code' and paste your snippet. Click Update." },
      { t: "Resize & Publish", d: "Drag the corners so the embed is at least 640px tall. Click Publish at the top-right." },
    ],
  },
  {
    id: "webflow",
    name: "Webflow",
    steps: [
      { t: "Open the Designer", d: "Open the page where the widget should appear in the Webflow Designer." },
      { t: "Drag an Embed element", d: "From the Add panel (A key) drag an Embed component to the desired spot on the page." },
      { t: "Paste the snippet", d: "The HTML Embed Code editor opens automatically → paste your snippet → Save & Close." },
      { t: "Publish", d: "Hit Publish → choose your domain. Verify on the live site that the iframe renders at full width on mobile and desktop." },
    ],
  },
];

export default function Setup() {
  const [active, setActive] = useState("wordpress");
  const platform = PLATFORMS.find((p) => p.id === active) || PLATFORMS[0];

  const copy = () => {
    navigator.clipboard?.writeText(SNIPPET);
    toast.success("Snippet copied");
  };

  return (
    <div className="relative bg-[#0A1628] text-[#F8FAFC] py-16 sm:py-20">
      <div className="absolute inset-0 so-grid opacity-20 pointer-events-none" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] so-glow-blue pointer-events-none" />

      <div className="relative max-w-4xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="uppercase text-xs tracking-widest text-[#06B6D4] font-mono">Setup guide</div>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Paste your embed code in under 5 minutes.
          </h1>
          <p className="mt-4 text-[#94A3B8] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
            Pick your platform below and follow the four-step guide. The same
            embed snippet works everywhere.
          </p>
        </div>

        {/* Website-plan compatibility warning */}
        <div
          role="alert"
          data-testid="setup-plan-warning"
          className="mb-8 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-5 sm:p-6 text-left flex items-start gap-4"
        >
          <AlertTriangle
            className="text-amber-300 mt-0.5 shrink-0"
            size={22}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <div className="font-display text-base sm:text-lg font-bold tracking-tight text-amber-100">
              Before you start — check your website plan
            </div>
            <p className="mt-2 text-sm sm:text-[15px] leading-relaxed text-amber-100/85">
              Adding a custom widget requires your website platform to support
              custom code or iframe embeds. Most paid plans include this
              feature, but some entry-level plans do not. If you are unsure,
              check your current plan before proceeding — you may need to
              upgrade. Not sure? Contact your website provider or reply to
              your setup email and we will help you figure it out.
            </p>
          </div>
        </div>

        {/* Snippet card */}
        <div className="so-card rounded-2xl p-5 sm:p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="uppercase text-[11px] tracking-widest text-[#94A3B8] font-mono">
              Universal embed snippet
            </div>
            <button
              onClick={copy}
              data-testid="setup-copy-snippet"
              className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs"
            >
              <Copy size={12} /> Copy
            </button>
          </div>
          <pre className="bg-[#091324] border border-white/10 rounded-lg p-4 text-[12px] sm:text-[13px] font-mono text-[#06B6D4] overflow-x-auto leading-relaxed">
{SNIPPET}
          </pre>
          <p className="mt-3 text-[11px] text-[#94A3B8] font-mono">
            Replace <span className="text-[#06B6D4]">YOUR_WIDGET_ID</span> with the ID shown in your account.
          </p>
        </div>

        {/* Platform tabs */}
        <div className="mt-10">
          <div className="flex items-center gap-2 overflow-x-auto -mx-6 px-6 pb-1">
            {PLATFORMS.map((p) => (
              <button
                key={p.id}
                onClick={() => setActive(p.id)}
                data-testid={`setup-tab-${p.id}`}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                  active === p.id
                    ? "bg-[#06B6D4] text-[#0A1628] border-[#06B6D4]"
                    : "border-white/10 text-[#94A3B8] hover:text-[#F8FAFC] hover:border-[#06B6D4]/40"
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>

          <ol className="mt-6 space-y-3" data-testid={`setup-steps-${platform.id}`}>
            {platform.steps.map((s, i) => (
              <li key={i} className="so-card rounded-xl p-5 sm:p-6 flex gap-4">
                <div className="shrink-0">
                  <div className="w-9 h-9 rounded-full bg-[#06B6D4]/15 border border-[#06B6D4]/40 text-[#06B6D4] flex items-center justify-center font-mono text-sm font-bold">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="font-display text-base sm:text-lg font-bold tracking-tight">
                    {s.t}
                  </div>
                  <p className="mt-1.5 text-[#94A3B8] text-sm leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-8 so-card rounded-xl p-5 sm:p-6 flex items-start gap-3">
            <CheckCircle2 className="text-[#06B6D4] mt-0.5 shrink-0" size={18} />
            <div className="text-sm text-[#94A3B8] leading-relaxed">
              <span className="text-[#F8FAFC] font-medium">Stuck?</span>{" "}
              Email <a className="text-[#06B6D4] hover:underline" href="mailto:support@genquotes.com">support@genquotes.com</a> — we're happy to help.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
